import React, { useState, useEffect, useCallback } from 'react';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAuth } from '../../../../../context/AuthUserContext';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  setInviteId,
  removeInviteId,
  claimVenue,
} from '../../../../../lib/apis/claim';
import { localeFiles } from '../../../../../data/constants';
import {
  toLocationStr,
  getErrorMsg,
  serializePrismaObject,
} from '../../../../../lib/utils';
import PageDefault from '../../../../../components/templates/PageDefault';
import VenueDetails from '../../../../../components/templates/VenueDetails';
import AccountLayer from '../../../../../components/organisms/AccountLayer';
import Button from '../../../../../components/atoms/Button';
import RequestForm from '../../../../../components/organisms/RequestForm';
import styles from '../../../../../styles/pages/Venue.module.scss';
import { prisma } from '../../../../../db';
import { HTTP_URL } from '../../../../../env.server';
import { Review } from '@prisma/client';

interface VenueProps {
  venue: any;
  otherVenues: any;
  origin: string;
  referer?: string;
  isClaimed: boolean;
}

const Venue = (props: VenueProps) => {
  const itemType = 'venue';
  const { t } = useTranslation(['common', itemType]);
  const { user } = useAuth();
  const router = useRouter();
  const [layer, setLayer] = useState() as any;
  const [fullLayer, setFullLayer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [isVenueAdmin, setIsVenueAdmin] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [error, setError] = useState() as any;
  const { query } = router;
  const { invite_code, autoClaim } = query as any;
  const { venue, otherVenues } = props;
  const { id, name, admins, description, location } = venue;
  const { address } = location || {};

  /*
   * Claim
   */
  const claim = useCallback(
    (venueID) => {
      if (claiming || !user || !!claimed) {
        return;
      }
      console.log('claim', venueID);
      setLoading(true);
      setClaiming(true);
      claimVenue(venueID)
        .then((response: any) => {
          if (response.status !== 200) {
            const errorMsg = getErrorMsg(response.data);
            setError(t(`common:form-error`) + ` (${errorMsg})`);
          } else if (user.onboarding_complete) {
            router.push(`/my-businesses`);
          }
        })
        .catch((error) => {
          const errorMsg = getErrorMsg(error);
          setError(t(`common:form-error`) + ` (${errorMsg})`);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [t, router, user, claimed, claiming]
  );

  /**
   * stores the invite ID to keep invitation context
   */
  useEffect(() => {
    if (!!invite_code && id) {
      setInviteId(id, invite_code);
    }
  }, [invite_code, id]);

  /**
   * triggers autoClaim
   */
  useEffect(() => {
    if (
      user &&
      !user.venues.some((v) => v.venue_id === id) &&
      window.location.href.includes('autoClaim=1')
    ) {
      claim(id);
    }
  }, [user, claim, id]);

  /**
   * Check if user owns venue
   * Remove invite code if it still exists but venue is claimed
   */
  useEffect(() => {
    if (!!id && !!user) {
      if (
        user?.venues.some((v) => v.venue_id === id) ||
        user.email.includes('@venuescout.org')
      ) {
        setClaimed(true);
        setIsVenueAdmin(true);
        setLoading(false);
        if (!!invite_code || window.location.href.includes('autoClaim=1')) {
          removeInviteId(id);
          router.replace(router.asPath.split('?')[0], undefined, {
            shallow: true,
          });
        }
      }
    } else if (!user && props.isClaimed) {
      setClaimed(true);
    } else if (!user && !props.isClaimed) {
      setClaimed(false);
    }
  }, [id, admins, user, claimed, invite_code, router, props.isClaimed]);

  /*
   * Proceed to claim or login
   */
  const onClaim = () => {
    if (!!user) {
      claim(id);
    } else {
      setLayer({
        closeBtn: false,
        bg: false,
        children: (
          <AccountLayer
            currView={'login'}
            toggle={() => setLayer({ children: null })}
            redirect={`${props.origin}/venue/${props.venue.country}/${
              props.venue.city
            }/${props.venue.slug}?invite_code=${invite_code || ''}&autoClaim=1`}
          />
        ),
      });
    }
  };

  /**
   * Sets the claim venue toast
   */
  const toastClasses = clsx(
    { [styles.error]: !!error },
    { [styles.loading]: loading }
  );

  const toast =
    !claimed && !!invite_code && !!id && !loading
      ? {
          closeBtn: !!error,
          children: (
            <>
              {!!error ? (
                <small className={toastClasses}>
                  <b>{error}</b>
                </small>
              ) : (
                <small
                  className={toastClasses}
                  title={t(`${itemType}:claim-info`)}
                >
                  <b>{name}</b>
                  <span>, {toLocationStr(address, true)}</span>
                  <br />
                  {t(`${itemType}:claim-info-short`)}
                </small>
              )}
              <div>
                <small>
                  <Link href="/what-is-venueScout">
                    <a>{t('pages:what-is-venueScout:navi')}</a>
                  </Link>
                </small>
                <Button
                  dark
                  large
                  blocked={!!error}
                  icon="venue"
                  label={t(`${itemType}:cta-claim`)}
                  onClick={onClaim}
                />
              </div>
            </>
          ),
        }
      : undefined;

  /**
   * Sets a button with anchor link
   */
  const anchorBtn =
    !!toast || loading
      ? //|| (!!user && claimed)
        undefined
      : {
          label: t(`${itemType}:cta-proposal`),
          el: 'contact',
          icon: 'proposal',
          onClick: () =>
            setLayer({
              bg: true,
              large: true,
              children: (
                <RequestForm
                  type={itemType}
                  data={venue}
                  onSubmit={() => setLayer(null)}
                />
              ),
            }),
        };

  return (
    <>
      <Head>
        <title>
          {name} | {t('common:app-name')}
        </title>
        <meta name="description" content={description || ''} />
      </Head>

      <PageDefault
        className={styles.venuePage}
        layer={layer}
        fullLayer={fullLayer}
        toast={toast}
        isLoading={loading || (autoClaim && !error)}
        anchor={anchorBtn}
      >
        <VenueDetails
          venue={venue}
          claim={!claimed && !props.isClaimed ? onClaim : undefined}
          claimed={claimed}
          isVenueAdmin={isVenueAdmin}
          setLayer={setLayer}
          setFullLayer={setFullLayer}
          referer={props.referer}
          otherVenues={props.otherVenues}
        />
      </PageDefault>
    </>
  );
};

export default Venue;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { params: { slug, country, city } = {}, locale } = context;

  try {
    const venue = await prisma.venue.findUnique({
      where: {
        country_city_slug: {
          slug: String(slug),
          city: String(city),
          country: String(country),
        },
      },
      include: {
        sales_contacts: {
          include: {
            image: true,
          },
        },
        venue_spaces: {
          include: {
            images: true,
          },
        },
        management_contacts: {
          include: {
            image: true,
          },
        },
        files: {
          select: {
            id: true,
            name: true,
            type: true,
            size: true,
            url: true,
            category: true,
            description: true,
            href: true,
          },
        },
        admins: true,
        primary_image: true,
        contact_details: true,
        location: true,
        reviews: true,
        images: true,
        subscriptions: true,
        service_providers: true,
      },
    });

    if (!venue || venue.deleted_at) {
      return {
        notFound: true,
      };
    }

    const service_providers = await prisma.venueServiceProvider.findMany({
      where: {
        venue_id: venue?.id,
      },
      include: {
        service_provider: true,
      },
    });

    const admins = venue.admins;
    const primaryAdmin = admins.find((a) => a.role === 'PRIMARY');

    let otherVenues = [];
    if (primaryAdmin) {
      const venues = await prisma.venueAdmin.findMany({
        where: {
          user_id: primaryAdmin.user_id,
        },
        include: {
          venue: {
            include: {
              images: true,
              admins: true,
            },
          },
        },
      });

      otherVenues.push(
        ...venues.filter((v) => v.venue.id !== venue.id).map((v) => v.venue)
      );
    }

    const serviceProvidersWithCategories: any[] = [];

    for (const sp of service_providers) {
      const categories = await prisma.serviceCategory.findMany({
        where: {
          service_provider_id: sp.service_provider_id,
        },
      });

      const types = categories.map((c) => c.type);
      serviceProvidersWithCategories.push({
        ...sp.service_provider,
        categories: types,
      });
    }

    const venueWithServiceProviders = {
      ...venue,
      service_providers: serviceProvidersWithCategories,
    };

    const reviews = venueWithServiceProviders.reviews.map((item: Review) => ({
      ...item,
      createdAt: JSON.parse(JSON.stringify(item.createdAt)),
    }));

    return {
      props: {
        origin: HTTP_URL,
        referer: context.req.headers.referer || '',
        venue: serializePrismaObject({ ...venueWithServiceProviders, reviews }),
        otherVenues: serializePrismaObject(otherVenues),
        isClaimed: admins.length > 0,
        ...(await serverSideTranslations(locale!, localeFiles)),
      },
    };
  } catch (e) {
    console.error(e);
  }
}
